/**
 * Single source of truth for the business-portal password policy.
 *
 * Previously signup used a whitelist regex — /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,16}$/ —
 * which rejected any character outside `a-zA-Z0-9!@#$%^&*` (e.g. `.`, `_`, `-`, `?`, spaces)
 * and anything over 16 characters, so most password-manager passwords and many
 * human ones could not be used to create an account.
 *
 * Policy (deliberately no character whitelist):
 *  - PASSWORD_MIN_LENGTH..PASSWORD_MAX_LENGTH characters, counted as Unicode code points.
 *    The minimum matches passport-server's BusinessResetPasswordDto (@MinLength(8)),
 *    so any password a user can sign up with can also be set again via reset.
 *  - at least one letter, one number, and one special character (anything that
 *    is not a letter or a number, including spaces and symbols).
 *  - never trimmed or normalised: the exact string typed is what gets hashed.
 */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export const PASSWORD_REQUIRED_MESSAGE = "Password is required";

export interface PasswordRule {
  id: "length" | "letter" | "number" | "special";
  /** Short label for the requirements hint, e.g. "a number". */
  label: string;
  /** Error shown when this rule is the first one failing. */
  message: string;
  test: (password: string) => boolean;
}

const characterCount = (password: string) => [...password].length;

export const passwordRules: readonly PasswordRule[] = [
  {
    id: "length",
    label: `${PASSWORD_MIN_LENGTH}+ characters`,
    message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
    test: (password) => characterCount(password) >= PASSWORD_MIN_LENGTH,
  },
  {
    id: "letter",
    label: "a letter",
    message: "Password must contain at least one letter",
    test: (password) => /\p{L}/u.test(password),
  },
  {
    id: "number",
    label: "a number",
    message: "Password must contain at least one number",
    test: (password) => /\p{N}/u.test(password),
  },
  {
    id: "special",
    label: "a special character",
    message: "Password must contain at least one special character",
    test: (password) => /[^\p{L}\p{N}]/u.test(password),
  },
];

export const PASSWORD_REQUIREMENTS_HINT = `Password must have ${passwordRules
  .map((rule) => rule.label)
  .join(", ")}.`;

/**
 * Returns the first applicable error message, or `undefined` when the
 * password satisfies the policy.
 */
export function getPasswordError(password: string): string | undefined {
  if (password.length === 0) return PASSWORD_REQUIRED_MESSAGE;

  if (characterCount(password) > PASSWORD_MAX_LENGTH) {
    return `Password must be at most ${PASSWORD_MAX_LENGTH} characters`;
  }

  return passwordRules.find((rule) => !rule.test(password))?.message;
}

/** react-hook-form `register` options enforcing the policy. */
export const newPasswordValidation = {
  validate: (value: string) => getPasswordError(value ?? "") ?? true,
} as const;
